import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

export const PENDING_INVITE_CODE_KEY = 'pendingInviteCode';

function getInviteCodeFromUrl(url: string): string | null {
  const [path, query = ''] = url.split('?');
  if (!path.endsWith('/invite')) {
    return null;
  }
  return new URLSearchParams(query).get('code');
}

/**
 * Guard that saves the invite code to localStorage BEFORE AuthGuard runs.
 * Must be placed on the /portal route, before AuthGuard, to capture the code
 * from the URL before the Auth0 redirect happens.
 */
export const saveInviteCodeFromUrlGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean => {
  const code = getInviteCodeFromUrl(state.url);

  if (code) {
    localStorage.setItem(PENDING_INVITE_CODE_KEY, code);
  }

  // Always return true - this guard only saves the code, doesn't block navigation
  return true;
};

/**
 * Guard that saves the invite code to localStorage before the AuthGuard redirects to Auth0.
 * This allows us to redirect the user to the invite page after login/registration.
 */
export const saveInviteCodeGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const code = route.queryParamMap.get('code');

  if (code) {
    localStorage.setItem(PENDING_INVITE_CODE_KEY, code);
  }

  // Always return true - this guard only saves the code, doesn't block navigation
  return true;
};

/**
 * Guard that checks for a pending invite code and redirects to the invite page.
 * Should be used on the user-entry route to intercept before RedirectionComponent loads.
 */
export const pendingInviteRedirectGuard: CanActivateFn = (): boolean | UrlTree => {
  const router = inject(Router);
  const pendingCode = localStorage.getItem(PENDING_INVITE_CODE_KEY);

  if (pendingCode) {
    // Clear the code immediately to prevent redirect loops
    localStorage.removeItem(PENDING_INVITE_CODE_KEY);
    // Return UrlTree to redirect - this is the recommended way in Angular
    return router.createUrlTree(['/portal/organization-mgmt/invite'], { queryParams: { code: pendingCode } });
  }

  return true;
};

/**
 * Helper function to get the pending invite code from localStorage
 */
export function getPendingInviteCode(): string | null {
  return localStorage.getItem(PENDING_INVITE_CODE_KEY);
}

/**
 * Helper function to clear the pending invite code from localStorage
 */
export function clearPendingInviteCode(): void {
  localStorage.removeItem(PENDING_INVITE_CODE_KEY);
}
